import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import {
    FileText, Search, RefreshCw
} from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import { searchKey } from '../lib/utils';
import BillSheet, { displayInvoiceNumber, formatDate, formatEur } from '../components/BillSheet';

/**
 * Bills page - compact list of synced Odoo vendor bills.
 * Click a bill to see its lines in a modal.
 */
const Invoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const [selectedBill, setSelectedBill] = useState(null);

    useEffect(() => {
        document.title = 'Factures | Costy';
        fetchInvoices();
    }, []);

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('invoices/');
            const data = response.data;
            setInvoices(Array.isArray(data) ? data : data?.results ?? []);
        } catch (error) {
            console.error('Failed to fetch invoices:', error);
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredInvoices = invoices.filter(inv =>
        searchKey(inv.invoice_number).includes(searchKey(searchTerm)) ||
        searchKey(inv.supplier_name).includes(searchKey(searchTerm))
    );

    return (
        <Layout>
            <div className="space-y-5">
                {/* Header */}
                <PageHeader title="Factures" subtitle={`${filteredInvoices.length} factures importées${searchTerm ? ' (filtrées)' : ''}`}>
                    <button onClick={fetchInvoices} className="btn-outline">
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        Actualiser
                    </button>
                </PageHeader>

                {/* Search */}
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Rechercher un numéro ou un fournisseur…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                    />
                </div>

                {/* Compact bills table */}
                <div className="panel overflow-hidden">
                    {loading ? (
                        <div className="p-10 text-center text-muted-foreground text-sm">Chargement…</div>
                    ) : filteredInvoices.length === 0 ? (
                        <div className="p-10 text-center text-muted-foreground">
                            <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                            <p className="text-sm">Aucune facture. Importez-les depuis Fournisseurs.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/50 border-b border-border text-[11px] font-medium text-muted-foreground">
                                    <tr>
                                        <th className="px-4 py-2 text-left font-medium">Facture</th>
                                        <th className="hidden sm:table-cell px-4 py-2 text-left font-medium">Fournisseur</th>
                                        <th className="hidden sm:table-cell px-4 py-2 text-left font-medium">Date</th>
                                        <th className="px-4 py-2 text-right font-medium">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {filteredInvoices.map((invoice) => (
                                        <tr
                                            key={invoice.id}
                                            onClick={() => setSelectedBill(invoice)}
                                            className="hover:bg-muted/40 cursor-pointer transition-colors"
                                        >
                                            <td className="px-4 py-3 font-semibold text-foreground">
                                                {displayInvoiceNumber(invoice)}
                                                <span className="sm:hidden block text-xs font-normal text-muted-foreground">{invoice.supplier_name}, {formatDate(invoice.invoice_date)}</span>
                                            </td>
                                            <td className="hidden sm:table-cell px-4 py-2 text-muted-foreground">{invoice.supplier_name}</td>
                                            <td className="hidden sm:table-cell px-4 py-2 text-muted-foreground whitespace-nowrap">{formatDate(invoice.invoice_date)}</td>
                                            <td className="px-4 py-3 text-right price whitespace-nowrap">
                                                {formatEur(invoice.total_amount)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {selectedBill && <BillSheet bill={selectedBill} onClose={() => setSelectedBill(null)} />}
        </Layout>
    );
};

export default Invoices;
